import { useEffect, useMemo, useState } from 'react';
import {
    ButtonSizes,
    ButtonStyles,
    Checkbox,
    Icon,
    Icons,
    Label,
    List,
    ListItem,
    MenuButton,
    RadioGroup,
    Search,
    Tabs,
    Toggle,
    Tooltip
} from '@silverfort/sf-components';
import {
    FENCING_TABS,
    HTTP_OPTIONS,
    SEARCH_MIN_CHARS,
    SERVICE_ACCOUNTS_PROTOCOLS_OPTIONS
} from '@silverfort-nodelibs/common/utils/consts';
import {
    AdTypeOptions,
    calcItemSubtitle,
    calcResourceIcon
} from '../../../components/Controls/ResourcePicker/ResourcePickerConsts';
import ResourcePicker from '../../../components/Controls/ResourcePicker/ResourcePicker';
import ServicePicker from '../components/ServicePicker';
import ProtocolPicker from '../components/ProtocolPicker';
import { httpGet } from '../../../components/utils';
import Loader from '../../../components/Controls/Loader/Loader';
import { DEFAULT_FENCING_POLICY } from '../consts';

const getDefaultFencingPolicy = () => {
    return JSON.parse(JSON.stringify(DEFAULT_FENCING_POLICY));
};

const DEFAULT_AD_TYPES = Object.values(AdTypeOptions);

const defaultDestinationMap = {
    Krbtgt: 'krbtgt is mandatory part in authenticating via Kerberos',
    cifs: 'cifs service is mandatory part of remote connection',
    LDAP: 'LDAP service is mandatory part of remote connection',
    host: 'host service is mandatory part of remote connection',
    'Protected storage': 'Protected storage service is mandatory part of remote connection'
};

const FencingPolicy = ({
    account,
    editedPolicy = getDefaultFencingPolicy(),
    setEditedPolicy,
    setOriginalPolicy,
    isEditor,
    isActiveOnly = false
}) => {
    const [selectedTab, setSelectedTab] = useState(FENCING_TABS[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [openManualSelection, setOpenManualSelection] = useState();
    const [openDetectedSelection, setOpenDetectedSelection] = useState();
    const [matchedResources, setMatchedResources] = useState([]);
    const [matchedIps, setMatchedIps] = useState([]);
    const [matchedDetectedResources, setMatchedDetectedResources] = useState([]);
    const [matchedDetectedIps, setMatchedDetectedIps] = useState([]);
    const [resourceFilters, setResourceFilters] = useState({});
    const [loadingResources, setLoadingResources] = useState();
    const [loadingPolicy, setLoadingPolicy] = useState();
    const [newServiceName, setNewServiceName] = useState('');
    const [searchRequestId, setSearchReauestId] = useState(0);
    const [detectedBySilverfort, setDetectedBySilverfort] = useState({ Sources: {}, Destinations: {} });

    const sourcesOrDestinations =
        selectedTab.name === FENCING_TABS[0].name || selectedTab.name === FENCING_TABS[1].name;

    const fencingDisabled = !isEditor;

    useEffect(() => {
        isActiveOnly && setEditedPolicy({ ...editedPolicy, protected: true });
    }, [isActiveOnly]);

    const resetDetectedMatch = () => {
        if (sourcesOrDestinations) {
            setMatchedDetectedIps(Object.values(detectedBySilverfort[selectedTab.name]).filter((item) => !item.guid));
            setMatchedDetectedResources(
                Object.values(detectedBySilverfort[selectedTab.name]).filter((item) => item.guid)
            );
        }
    };

    const searchResources = async () => {
        try {
            let escapedQuery = resourceFilters.searchTerm?.trim() || '';

            if (escapedQuery === '' || escapedQuery.length < SEARCH_MIN_CHARS) {
                setMatchedResources([]);
            } else {
                escapedQuery = encodeURIComponent(escapedQuery);
                const newSearchRequestId = searchRequestId + 1;

                setSearchReauestId(newSearchRequestId);
                setLoadingResources(true);

                const baseSearchURL = '/users/searchEntitiesByTypes';
                const reqUrl = `${baseSearchURL}/${escapedQuery}/${newSearchRequestId}`;

                const res = await httpGet(reqUrl, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        params: { hideDeleted: true, entitiesTypes: resourceFilters.adTypes || DEFAULT_AD_TYPES }
                    }
                });

                setMatchedResources(res.data?.Results || []);
            }
        } catch (e) {
            console.error('Failed to search resources for query ' + resourceFilters.searchTerm, e);
        } finally {
            setLoadingResources(false);
        }
    };

    useEffect(() => {
        const fetchFencingPolicy = async () => {
            try {
                const responses = await Promise.all([
                    httpGet(`/fencing/${account.guid}`),
                    httpGet(`/privilegedAccounts/detectedBySilverfort/${account.guid}`)
                ]);

                if (responses[0]?.data?.policy) {
                    //deep copy original policy
                    const original = responses[0].data.policy;
                    const protocols = Object.values(SERVICE_ACCOUNTS_PROTOCOLS_OPTIONS).filter((p) =>
                        original.Protocols.includes(p.value)
                    );

                    original.Protocols = protocols;
                    const edited = { ...original };

                    edited.Protocols = [...protocols];
                    edited.Destinations = [...original.Destinations];
                    edited.Sources = [...original.Sources];

                    setEditedPolicy(edited);
                    setOriginalPolicy(original);
                } else {
                    setEditedPolicy(getDefaultFencingPolicy());
                }

                if (responses[1]?.data) {
                    setDetectedBySilverfort(responses[1].data);
                }
            } catch (error) {
                console.error('failed to load virtual fencing policy', error);
            } finally {
                setLoadingPolicy(false);
            }
        };
        if (account?.guid) {
            setLoadingPolicy(true);
            fetchFencingPolicy();
        }
    }, []);

    useEffect(() => {
        resetDetectedMatch();
    }, [selectedTab, detectedBySilverfort]);

    useEffect(() => {
        if (sourcesOrDestinations) {
            setMatchedIps(editedPolicy[selectedTab.name]?.filter((item) => !item.guid));
            searchResources();
        }
    }, [resourceFilters, selectedTab]);

    const closeModals = () => {
        setOpenDetectedSelection(false);
        setOpenManualSelection(false);
        setNewServiceName('');
        setResourceFilters({});
        resetDetectedMatch();
    };

    const calcDetectedIcon = (item) => {
        if (
            sourcesOrDestinations &&
            (detectedBySilverfort[selectedTab.name][item.guid] || detectedBySilverfort[selectedTab.name][item])
        ) {
            return () => (
                <div>
                    <Tooltip title="Detected by Silverfort" placement="top">
                        <Icon icon={Icons.CircleCheck} className="text-sf-primary-500" />{' '}
                    </Tooltip>
                </div>
            );
        }
    };

    const handleResourceRemoval = (item) => {
        const newItems = editedPolicy[selectedTab.name].filter((currItem) => {
            if (item.guid) {
                return currItem.guid !== item.guid;
            }
            return currItem !== item;
        });

        setEditedPolicy({ ...editedPolicy, [selectedTab.name]: newItems });
    };

    const calcItemTooltip = (item) => {
        if (selectedTab.name === 'Protocols' && editedPolicy.Protocols?.length === 1) {
            return 'At least one protocol must be allowed';
        }
    };

    const handleAddAllDetected = () => {
        const newItems = [...editedPolicy[selectedTab.name]];
        const currentKeys = new Set([...newItems.map((item) => (item.guid ? item.guid : item))]);

        for (const key in detectedBySilverfort[selectedTab.name]) {
            if (!currentKeys.has(key)) {
                newItems.push(detectedBySilverfort[selectedTab.name][key]);
            }
        }

        setEditedPolicy({ ...editedPolicy, [selectedTab.name]: newItems });
        closeModals();
    };

    const handleResourceSelect = (item, type) => {
        const newItems = [...editedPolicy[selectedTab.name]];

        if (type === 'resources') {
            if (!newItems.find((resource) => resource.guid === item.guid)) {
                newItems.push(item);
            }
        } else {
            if (!newItems.find((stringItem) => stringItem === item)) {
                newItems.push(item);
            }
        }

        setEditedPolicy({
            ...editedPolicy,
            [selectedTab.name]: newItems
        });
    };

    const handleResourceSearch = (val, type) => {
        if (type === 'ips') {
            setMatchedDetectedIps(
                Object.values(detectedBySilverfort[selectedTab.name]).filter(
                    (item) => !item.guid && item.startsWith(val)
                )
            );

            setMatchedIps(editedPolicy[selectedTab.name]?.filter((item) => !item.guid && item.startsWith(val)));
        } else {
            setMatchedDetectedResources(
                Object.values(detectedBySilverfort[selectedTab.name]).filter((item) =>
                    item.name?.toLowerCase().includes(val.toLowerCase())
                )
            );

            setResourceFilters({ ...resourceFilters, searchTerm: val });
        }
    };

    const defaultItems = useMemo(() => {
        return selectedTab?.name !== 'Protocols' && editedPolicy && !editedPolicy['allowAllDestinations']
            ? defaultDestinationMap
            : {};
    }, [selectedTab, editedPolicy]);

    const filteredItems = useMemo(() => {
        const defaultMappedItems = Object.keys(defaultItems).map((item) => ({ name: item, label: item }));
        return [...editedPolicy[selectedTab.name], ...defaultMappedItems]?.filter((item) =>
            item.label
                ? item.label.toLowerCase().includes(searchTerm.toLowerCase())
                : item.name
                ? item.name.toLowerCase().includes(searchTerm.toLowerCase())
                : item.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [editedPolicy, searchTerm, selectedTab, defaultItems]);

    const limitedSelectionTooltip = useMemo(() => {
        return selectedTab.name === 'Protocols'
            ? ''
            : 'Some background connections are allowed automatically by default in your resource list to ensure seamless access via Kerberos. This will not impact your manually defined Virtual Fencing policies';
    }, [selectedTab]);

    return loadingPolicy ? (
        <div className="w-full h-64 flex items-center justify-center">
            <Loader />
        </div>
    ) : (
        <div className="px-2 font-sf text-sf-gray-800">
            <div className="border flex justify-between rounded-lg border-sf-gray-100 h-18">
                <div className="flex justify-between items-center gap-4">
                    <Icon
                        size={24}
                        className="border border-sf-gray-100 rounded-lg my-4 ml-4 p-2 text-sf-gray-400"
                        icon={Icons.ShieldAdd}></Icon>
                    <Label className=" text-16" text="Activate Virtual Fencing Policy"></Label>
                </div>
                {!isActiveOnly && (
                    <Toggle
                        disabled={fencingDisabled}
                        large="true"
                        className="my-6 justify-self-end mr-4"
                        checked={editedPolicy?.protected}
                        onChange={() =>
                            setEditedPolicy({ ...editedPolicy, protected: !editedPolicy?.protected })
                        }></Toggle>
                )}
            </div>
            <div className="py-4 flex flex-col gap-4">
                <Tabs
                    tabs={FENCING_TABS}
                    selectedTab={selectedTab.value}
                    onClick={(value) => {
                        closeModals();
                        setSelectedTab(FENCING_TABS[value]);
                    }}
                />
                <div className="flex flex-col gap-2">
                    <div className="h-10 flex items-center">
                        <RadioGroup
                            disabled={fencingDisabled}
                            options={[
                                { title: 'Unrestricted', value: true },
                                {
                                    title: `Limit to selected ${selectedTab.name.toLowerCase()}`,
                                    value: false,
                                    tooltipInfo: limitedSelectionTooltip
                                }
                            ]}
                            onChange={(value) => {
                                setEditedPolicy({
                                    ...editedPolicy,
                                    [`allowAll${selectedTab.name}`]: value === 'true'
                                });
                            }}
                            selectedOption={
                                !!(editedPolicy && editedPolicy[`allowAll${selectedTab.name}`])
                            }></RadioGroup>
                    </div>
                    {!editedPolicy[`allowAll${selectedTab.name}`] && (
                        <div className="border border-sf-gray-100 rounded-lg w-full h-[288px]">
                            <div className="py-2 pr-4 pl-6 border-b h-14 w-full flex items-center justify-between">
                                <Label
                                    text={`Selected ${selectedTab.name.toLowerCase()} (${
                                        editedPolicy[selectedTab.name]?.length || 0
                                    })`}
                                />
                                <div className="flex gap-2 items-center">
                                    <Search border={true} value={searchTerm} onChange={setSearchTerm} />
                                    <MenuButton
                                        disabled={editedPolicy[`allowAll${selectedTab.name}`] || fencingDisabled}
                                        buttonSize={ButtonSizes.Medium}
                                        icon={Icons.Add}
                                        iconClassName={`w-fit`}
                                        className="right-0"
                                        text="Add"
                                        holdPressed={openManualSelection || openDetectedSelection}
                                        buttonStyle={ButtonStyles.Primary}
                                        content={
                                            <List className="p-[0px]">
                                                <ListItem
                                                    title="Add manually"
                                                    IconLeft={() => (
                                                        <Icon icon={Icons.UserCheck} className="text-sf-gray-400" />
                                                    )}
                                                    onClick={() => {
                                                        setOpenDetectedSelection(false);
                                                        setOpenManualSelection(true);
                                                    }}
                                                />
                                                {sourcesOrDestinations && (
                                                    <ListItem
                                                        title="Detected by Silverfort"
                                                        IconLeft={() => (
                                                            <Icon
                                                                icon={Icons.CircleCheck}
                                                                className="text-sf-gray-400"
                                                            />
                                                        )}
                                                        onClick={() => {
                                                            setOpenManualSelection(false);
                                                            setOpenDetectedSelection(true);
                                                        }}
                                                    />
                                                )}
                                            </List>
                                        }
                                    />
                                </div>
                            </div>
                            {(openDetectedSelection || openManualSelection) && (
                                <div className="relative">
                                    {sourcesOrDestinations && (
                                        <div className="w-full z-10 absolute -top-[56px] left-[93px]">
                                            <ResourcePicker
                                                loading={loadingResources}
                                                title={
                                                    openManualSelection
                                                        ? 'Available resources'
                                                        : 'Resources detected by Silverfort'
                                                }
                                                showAddAll={openDetectedSelection}
                                                onAddAll={handleAddAllDetected}
                                                onSelect={handleResourceSelect}
                                                hideNewIPButton={openDetectedSelection}
                                                resources={
                                                    openDetectedSelection ? matchedDetectedResources : matchedResources
                                                }
                                                ips={openDetectedSelection ? matchedDetectedIps : matchedIps}
                                                selectedResources={editedPolicy[selectedTab.name]?.filter(
                                                    (item) => item.guid
                                                )}
                                                selectedIPs={editedPolicy[selectedTab.name]?.filter(
                                                    (item) => !item.guid
                                                )}
                                                onSearchChange={handleResourceSearch}
                                                showAdTypeFilter={openManualSelection}
                                                showNamingFilter={openManualSelection}
                                                onAdTypeChange={(types) =>
                                                    setResourceFilters({ ...resourceFilters, adTypes: types })
                                                }
                                                onAction={() => closeModals()}
                                                onClose={() => closeModals()}
                                            />
                                        </div>
                                    )}
                                    {selectedTab.name === 'Services' && (
                                        <ServicePicker
                                            serviceName={newServiceName}
                                            onServiceNameChange={setNewServiceName}
                                            onClose={closeModals}
                                            onSave={() => {
                                                if (!editedPolicy[selectedTab.name]?.includes(newServiceName)) {
                                                    setEditedPolicy({
                                                        ...editedPolicy,
                                                        [selectedTab.name]: [
                                                            ...(editedPolicy[selectedTab.name] || []),
                                                            newServiceName
                                                        ]
                                                    });
                                                }
                                                closeModals();
                                            }}
                                        />
                                    )}
                                    {selectedTab.name === 'Protocols' && (
                                        <ProtocolPicker
                                            selectedProtocols={editedPolicy[selectedTab.name]}
                                            onSave={(protocols) => {
                                                setEditedPolicy({ ...editedPolicy, [selectedTab.name]: protocols });
                                                closeModals();
                                            }}
                                            onClose={closeModals}
                                        />
                                    )}
                                </div>
                            )}
                            {!!filteredItems?.length ? (
                                <div className="h-[230px] w-full overflow-auto">
                                    <List>
                                        {filteredItems.map((item, i) => {
                                            return Object.hasOwn(defaultItems, item.name) ? (
                                                <Tooltip key={i} title={defaultItems[item.name]} placement="top-start">
                                                    <ListItem
                                                        disabled
                                                        title={item.name}
                                                        IconLeft={Icons.Server}
                                                        IconRight={Icons.CircleCheck}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip key={i} title={calcItemTooltip()} placement="top">
                                                    <ListItem
                                                        className="cursor-default"
                                                        title={
                                                            selectedTab.name === 'Protocols'
                                                                ? item.label
                                                                : item.name || item
                                                        }
                                                        subtitle={sourcesOrDestinations ? calcItemSubtitle(item) : ''}
                                                        IconLeft={
                                                            sourcesOrDestinations
                                                                ? calcResourceIcon(item, !item.guid)
                                                                : undefined
                                                        }
                                                        IconRight={calcDetectedIcon(item)}
                                                        onRemove={
                                                            fencingDisabled
                                                                ? undefined
                                                                : () => {
                                                                      const ignoreRemove =
                                                                          editedPolicy[`allowAll${selectedTab.name}`] ||
                                                                          (selectedTab.name === 'Protocols' &&
                                                                              editedPolicy[selectedTab.name]?.length ===
                                                                                  1);

                                                                      !ignoreRemove && handleResourceRemoval(item);
                                                                  }
                                                        }
                                                        disabled={
                                                            editedPolicy[`allowAll${selectedTab.name}`] ||
                                                            (selectedTab.name === 'Protocols' &&
                                                                editedPolicy[selectedTab.name]?.length === 1)
                                                        }
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </List>
                                </div>
                            ) : (
                                <div className="flex flex-col text-center h-[230px] w-full items-center justify-center">
                                    <div className="h-11 flex flex-col justify-between">
                                        <div className="font-medium text-16 text-sf-gray-800">
                                            {searchTerm ? 'No Results' : `No ${selectedTab.name} yet`}
                                        </div>
                                        <div className="text-13 text-sf-gray-500">
                                            {searchTerm
                                                ? 'Try refining your search'
                                                : ` Add items by clicking the “Add” button`}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-2">
                <Label text="For all other resources" />
                <div className="flex p-4 justify-between items-center border border-sf-gray-100 rounded-lg w-full h-12">
                    <div className="flex h-4 gap-6 text-13 font-sf-gray-800">
                        <span className="flex items-center gap-1">
                            <Checkbox
                                disabled={fencingDisabled}
                                checked={editedPolicy?.denyAccess}
                                onChange={() => {
                                    setEditedPolicy({ ...editedPolicy, denyAccess: !editedPolicy?.denyAccess });
                                }}
                            />
                            Deny access
                        </span>
                        <span className="flex items-center gap-1">
                            <Checkbox
                                disabled={fencingDisabled}
                                checked={editedPolicy?.sendToSiem}
                                onChange={() => {
                                    setEditedPolicy({ ...editedPolicy, sendToSiem: !editedPolicy?.sendToSiem });
                                }}
                            />
                            Send to SIEM
                        </span>
                    </div>
                    <div className="flex items-center gap-1 text-sf-gray-400">
                        <Icon icon={Icons.CircleInfo} /> Notifications will be sent automatically
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FencingPolicy;
